import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useT } from "../i18n/hooks.ts";
import { groupByPeriod, GRANULARITY_PERIOD_KEY, type Granularity } from "../model/aggregates.ts";
import { isoToDdmmyyyy } from "../model/isoDate.ts";

/**
 * Contribution of an already-aggregated entity (outlet point or zone — see
 * src/engine/aggregation.ts), bucketed at `granularity`
 * (CalculationResults.tsx's own selector), unlike ContributionChart.tsx (a
 * single sub-basin, in mm, converted right here). `volume` is already in
 * volume/day (with the units coefficient already applied), no need to
 * multiply by anything else.
 */
export function AggregatedContributionChart({
  title,
  volume,
  dates,
  unit,
  granularity,
}: {
  title: string;
  volume: Float64Array;
  dates: string[];
  unit: string;
  granularity: Granularity;
}) {
  const t = useT();
  // Same gap as ContributionChart.tsx — see its own comment.
  const data = useMemo(
    () =>
      groupByPeriod(dates, volume, granularity).map((r) => ({
        period: r.period,
        hm3: r.totalMm,
      })),
    [dates, volume, granularity]
  );

  return (
    <div className="chart">
      <h3>
        {t("contributionChart.title", {
          title,
          unit,
          period: t(GRANULARITY_PERIOD_KEY[granularity]),
        })}
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        {granularity === "daily" ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="period" tickFormatter={(iso: string) => isoToDdmmyyyy(iso)} />
            <YAxis />
            <Tooltip formatter={(v) => Number(v).toFixed(2)} />
            <Line
              type="monotone"
              dataKey="hm3"
              name={unit}
              stroke="#3fa7b3"
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(v) => Number(v).toFixed(2)} />
            <Bar dataKey="hm3" name={unit} fill="#3fa7b3" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
